import { store } from "@/store";
import { deleteCookie, getCookie, setCookie } from "cookies-next";
import ENDPOINTS from "./endpoints";
import { getRobotsMetadata } from "./app-environment";
import dayjs from "dayjs";
import "dayjs/locale/en";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import axios from "axios";
import toast from "react-hot-toast";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import Badge from "@/app/_badges/Badge";
import CryptoJS from "crypto-js";
import {
  CROSSBORDER_REQUEST_SIGNATURE_PLAINTEXT,
  consumeSigningPlaintextForEncryptedPayload,
  getSigningPlaintextForRequestBody,
} from "@/lib/crossborder-crypto";

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(timezone);
dayjs.extend(utc);

export const getFormData = (data) => {
  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        formData.append(`${key}[${index}]`, item);
      });
    } else {
      formData.append(key, value);
    }
  });
  return formData;
};

export const request = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  paramsSerializer: (params) => {
    const pairs = [];
    Object.entries(params).forEach(([key, value]) => {
      if (value == null) return;
      if (Array.isArray(value)) {
        value.forEach((item) => pairs.push([key, String(item)]));
        return;
      }
      pairs.push([key, String(value)]);
    });
    pairs.sort(([a], [b]) => a.localeCompare(b));
    return pairs
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join("&");
  },
});

export const ACCESS_TOKEN_MAX_AGE_SECONDS = 10 * 60;
export const REFRESH_TOKEN_MAX_AGE_SECONDS = 60 * 24 * 60 * 60;

export const setAccessTokenCookie = (accessToken) => {
  if (!accessToken) return;
  setCookie("access-token", accessToken, { maxAge: ACCESS_TOKEN_MAX_AGE_SECONDS });
};

export const setRefreshTokenCookie = (refreshToken) => {
  if (!refreshToken) return;
  setCookie("refresh-token", refreshToken, { maxAge: REFRESH_TOKEN_MAX_AGE_SECONDS });
};

export const setLoggedInCookie = (isLoggedIn = true) => {
  setCookie("is_logged_in", isLoggedIn, { maxAge: REFRESH_TOKEN_MAX_AGE_SECONDS });
};

export const clearAuthCookies = () => {
  deleteCookie("is_logged_in");
  deleteCookie("access-token");
  deleteCookie("refresh-token");
  deleteCookie("user_data");
};

const SECURE_MUTATION_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
const DEVICE_SCOPED_SECURITY_PATH_PATTERNS = [
  /\/api\/v2\/cross-border-payments\//,
  /\/api\/v2\/local-transfer\//,
  /\/api\/v2\/belyfted-transfer\//,
];
const IDEMPOTENCY_PATH_PATTERNS = [
  /\/api\/v2\/cross-border-payments\//,
  /\/api\/v2\/local-transfer\//,
  /\/api\/user\/send-money\//,
];
const REQUIRED_BOOLEAN_HEADER_KEY = "X-Requires-Secure-Flow";
const REQUIRED_BOOLEAN_HEADER_PATH_PATTERNS = [
  /\/api\/user\/send-money\//,
  /\/api\/v2\/cross-border-payments\//,
];
const DEVICE_ID_STORAGE_KEY = "belyfted_device_id";
const DEVICE_SECRET_STORAGE_KEY = "belyfted_device_secret";
const PAYLOAD_ENCRYPTION_KEY = process.env.NEXT_PUBLIC_PAYLOAD_ENCRYPTION_KEY;
const DEVICE_ID_FIELD_KEYS = ["device_id", "deviceId", "id", "uuid"];
const DEVICE_SECRET_FIELD_KEYS = [
  "secret",
  "device_secret",
  "deviceSecret",
  "secret_key",
  "secretKey",
  "hmac_secret",
  "hmacSecret",
  "signing_secret",
  "signingSecret",
  "api_secret",
  "apiSecret",
];

const generateUuid = () =>
  globalThis?.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

const getMethod = (method = "GET") => String(method).toUpperCase();

const buildPathAndQuery = (url = "", params = {}) => {
  const rawUrl = String(url || "");
  const [rawPath, rawQuery = ""] = rawUrl.split("?");
  const path = rawPath || "/";

  const pairs = [];
  const query = new URLSearchParams(rawQuery);
  query.forEach((value, key) => pairs.push([key, value]));

  if (params && typeof params === "object") {
    Object.entries(params).forEach(([key, value]) => {
      if (value == null) return;
      if (Array.isArray(value)) {
        value.forEach((item) => pairs.push([key, String(item)]));
        return;
      }
      pairs.push([key, String(value)]);
    });
  }

  pairs.sort(([a], [b]) => a.localeCompare(b));
  const queryString = pairs
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");

  return { path, queryString };
};

const normalizeBody = (data) => {
  if (data == null) return "[]";
  if (typeof FormData !== "undefined" && data instanceof FormData) return "[]";
  if (typeof URLSearchParams !== "undefined" && data instanceof URLSearchParams) {
    return data.toString();
  }
  if (typeof data === "string") {
    const trimmed = data.trim();
    if (!trimmed) return "[]";
    try {
      return JSON.stringify(JSON.parse(trimmed));
    } catch {
      return data;
    }
  }
  if (typeof data === "object") {
    try {
      return JSON.stringify(JSON.parse(JSON.stringify(data)));
    } catch {
      try {
        return JSON.stringify(data);
      } catch {
        return "[]";
      }
    }
  }
  return "[]";
};

const createSignature = ({
  method,
  path,
  queryString,
  timestamp,
  nonce,
  deviceId,
  body,
  secret,
}) => {
  const bodyHash = CryptoJS.SHA256(body).toString(CryptoJS.enc.Hex);
  const signingString = [method, path, queryString, timestamp, nonce, deviceId, bodyHash].join("|");
  return CryptoJS.HmacSHA256(signingString, secret).toString(CryptoJS.enc.Hex);
};

const shouldAttachIdempotencyKey = (path, method) => {
  if (!SECURE_MUTATION_METHODS.has(method)) return false;
  return IDEMPOTENCY_PATH_PATTERNS.some((pattern) => pattern.test(path));
};

const shouldUseDeviceScopedSecurity = (path) =>
  DEVICE_SCOPED_SECURITY_PATH_PATTERNS.some((pattern) => pattern.test(path));

const shouldAttachRequiredBooleanHeader = (path) =>
  REQUIRED_BOOLEAN_HEADER_PATH_PATTERNS.some((pattern) => pattern.test(path));

const getStoredDeviceSecret = () => {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(DEVICE_SECRET_STORAGE_KEY);
};

const pickValueByKeys = (source, keys = []) => {
  if (!source || typeof source !== "object") return "";
  for (const key of keys) {
    const value = source[key];
    if (value != null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return "";
};

const normalizeDeviceCredentials = (source, fallback = {}) => {
  const fallbackId = fallback.deviceId ?? fallback.id ?? "";
  const fallbackSecret = fallback.deviceSecret ?? fallback.secret ?? "";

  const deviceId = pickValueByKeys(source, DEVICE_ID_FIELD_KEYS) || String(fallbackId || "").trim();
  const deviceSecret =
    pickValueByKeys(source, DEVICE_SECRET_FIELD_KEYS) || String(fallbackSecret || "").trim();

  if (!deviceId || !deviceSecret) return null;
  return { deviceId, deviceSecret };
};

const persistDeviceCredentials = (credentials) => {
  if (typeof window === "undefined" || !credentials || typeof credentials !== "object") return;
  const normalized = normalizeDeviceCredentials(credentials, credentials);
  if (!normalized) return;
  const { deviceId, deviceSecret } = normalized;

  if (deviceId) {
    window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
  }
  if (deviceSecret) {
    window.localStorage.setItem(DEVICE_SECRET_STORAGE_KEY, deviceSecret);
  }
};

const clearDeviceCredentials = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(DEVICE_ID_STORAGE_KEY);
  window.localStorage.removeItem(DEVICE_SECRET_STORAGE_KEY);
};

const extractDeviceFromPayload = (payload) => {
  if (!payload || typeof payload !== "object") return null;

  const queue = [payload];
  const visited = new Set();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== "object" || visited.has(current)) continue;
    visited.add(current);

    const currentCredentials = normalizeDeviceCredentials(current);
    if (currentCredentials) {
      return currentCredentials;
    }

    if (current.device && typeof current.device === "object") {
      const mixedCredentials = normalizeDeviceCredentials(current.device, current);
      if (mixedCredentials) {
        return mixedCredentials;
      }
    }

    Object.values(current).forEach((value) => {
      if (value && typeof value === "object") {
        queue.push(value);
      }
    });
  }

  return null;
};

const decryptApiPayload = (encryptedPayload) => {
  if (!encryptedPayload || typeof encryptedPayload !== "string" || !PAYLOAD_ENCRYPTION_KEY) {
    return null;
  }

  try {
    const [base64Iv, base64Ciphertext] = encryptedPayload.split(":");
    if (!base64Iv || !base64Ciphertext) return null;

    const derivedKey = CryptoJS.SHA256(PAYLOAD_ENCRYPTION_KEY).toString(CryptoJS.enc.Hex);
    const key = CryptoJS.enc.Hex.parse(derivedKey);
    const iv = CryptoJS.enc.Base64.parse(base64Iv);
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: CryptoJS.enc.Base64.parse(base64Ciphertext) },
      key,
      { iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 },
    );
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    if (!decryptedString) return null;
    return JSON.parse(decryptedString);
  } catch {
    return null;
  }
};

const getUserAgentDeviceId = () => {
  if (typeof navigator === "undefined") return null;
  const userAgent = String(navigator.userAgent || "").trim();
  if (!userAgent) return null;

  // Backend requirement: device id is derived from user agent hash.
  return CryptoJS.SHA256(userAgent).toString(CryptoJS.enc.Hex);
};

const redirectToLoginIfNeeded = () => {
  if (typeof window === "undefined") return;
  const currentPath = window.location.pathname;
  if (!currentPath.includes("/login") && !currentPath.includes("/register")) {
    setTimeout(() => {
      window.location.href = basePath() + "/login";
    }, 100);
  }
};

let refreshTokenRequestPromise = null;

const refreshAccessToken = async () => {
  const storedRefreshToken = getCookie("refresh-token");
  if (!storedRefreshToken) {
    throw new Error("Missing refresh token");
  }

  if (!refreshTokenRequestPromise) {
    refreshTokenRequestPromise = axios
      .post(
        ENDPOINTS.AUTH_REFRESH,
        { refresh_token: storedRefreshToken },
        { baseURL: process.env.NEXT_PUBLIC_API_URL },
      )
      .then((response) => {
        const payload = response?.data?.data || {};
        const newAccessToken = payload.access_token || payload.accessToken;
        const newRefreshToken =
          payload.refresh_token || payload.refreshToken || storedRefreshToken;

        if (!newAccessToken) {
          throw new Error("No access token returned during refresh");
        }

        setAccessTokenCookie(newAccessToken);
        setRefreshTokenCookie(newRefreshToken);
        setLoggedInCookie(true);

        return newAccessToken;
      })
      .finally(() => {
        refreshTokenRequestPromise = null;
      });
  }

  return refreshTokenRequestPromise;
};

const resolveDeviceId = async () => {
  // Use backend-registered device id when available to avoid "unknown device".
  if (typeof window !== "undefined") {
    const existing = window.localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (existing) return existing;
  }

  // Fallback: deterministic user-agent derived id (useful before device registration).
  const userAgentDeviceId = getUserAgentDeviceId();
  if (userAgentDeviceId) return userAgentDeviceId;

  const fingerprint = await getFingerPrint();
  if (fingerprint) return fingerprint;

  if (typeof window === "undefined") return generateUuid();

  const created = generateUuid();
  window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, created);
  return created;
};

request.interceptors.request.use(
  async (config) => {
    try {
      config.headers = config.headers || {};
      const token = getCookie("access-token");
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      // If no token and we're making an authenticated request, don't fail immediately
      // Let the server respond with unauthenticated if needed

      const method = getMethod(config.method);
      const { path, queryString } = buildPathAndQuery(config.url, config.params);
      const timestamp = Math.floor(Date.now() / 1000).toString();
      const nonce = generateUuid();
      const deviceId = await resolveDeviceId();
      const isDeviceScopedRoute = shouldUseDeviceScopedSecurity(path);
      const deviceScopedSecret = isDeviceScopedRoute ? getStoredDeviceSecret() : null;
      const hmacSecret = isDeviceScopedRoute
        ? deviceScopedSecret
        : process.env.NEXT_PUBLIC_BELYFTED_HMAC_SECRET;

      config.headers["X-Timestamp"] = timestamp;
      config.headers["X-Nonce"] = nonce;
      config.headers["X-Device-ID"] = deviceId;
      config.headers["X-Fingerprint"] = deviceId;

      if (hmacSecret) {
        // Encrypted cross-border bodies: HMAC must use the same normalized inner JSON the server
        // hashes (Postman signs `jsonString` before wrapping in `{ payload: encrypted }`).
        const innerSigningPlaintext =
          (config.data &&
            typeof config.data === "object" &&
            (getSigningPlaintextForRequestBody(config.data) ||
              (typeof config.data[CROSSBORDER_REQUEST_SIGNATURE_PLAINTEXT] === "string"
                ? config.data[CROSSBORDER_REQUEST_SIGNATURE_PLAINTEXT]
                : null))) ||
          null;
        const usedCrossborderInnerPlaintext =
          typeof innerSigningPlaintext === "string" && innerSigningPlaintext.length > 0;

        let consumedEncryptPlaintext = null;
        if (
          !usedCrossborderInnerPlaintext &&
          config.data &&
          typeof config.data === "object" &&
          typeof config.data.payload === "string" &&
          config.data.payload.includes(":")
        ) {
          consumedEncryptPlaintext = consumeSigningPlaintextForEncryptedPayload(
            config.data.payload,
          );
        }

        const signaturePlaintext = usedCrossborderInnerPlaintext
          ? innerSigningPlaintext
          : consumedEncryptPlaintext ?? normalizeBody(config.data);
        const body = signaturePlaintext;

        if (
          usedCrossborderInnerPlaintext &&
          config.data &&
          typeof config.data.payload === "string" &&
          config.data.payload.includes(":")
        ) {
          consumeSigningPlaintextForEncryptedPayload(config.data.payload);
        }

        const signature = createSignature({
          method,
          path,
          queryString,
          timestamp,
          nonce,
          deviceId,
          body,
          secret: hmacSecret,
        });
        config.headers["X-Signature"] = signature;
      } else if (isDeviceScopedRoute && process.env.NODE_ENV !== "production") {
        console.warn("[request signature][missing-secret]", {
          path,
          hint: "Device secret is missing. Re-login to refresh device credentials.",
        });
      }

      if (config.headers["Idempotency-Key"] && !config.headers["X-Idempotency-Key"]) {
        config.headers["X-Idempotency-Key"] = config.headers["Idempotency-Key"];
        // Keep the original header — some endpoints (e.g. subscription) check
        // Idempotency-Key without the X- prefix.
      }

      if (shouldAttachIdempotencyKey(path, method) && !config.headers["X-Idempotency-Key"]) {
        config.headers["X-Idempotency-Key"] = generateUuid();
      }

      if (
        shouldAttachRequiredBooleanHeader(path) &&
        config.headers[REQUIRED_BOOLEAN_HEADER_KEY] == null
      ) {
        config.headers[REQUIRED_BOOLEAN_HEADER_KEY] = "true";
      }

      return config;
    } catch (error) {
    
      return config;
    }
  },
  (error) => {
    return Promise.reject(error);
  },
);

request.interceptors.response.use(
  (response) => {
    let extractedDevice = extractDeviceFromPayload(response?.data);
    if (!extractedDevice && response?.data?.payload) {
      const decryptedPayload = decryptApiPayload(response.data.payload);
      extractedDevice = extractDeviceFromPayload(decryptedPayload);
    }
    if (extractedDevice) {
      persistDeviceCredentials(extractedDevice);
    }

    if (response?.data?.remark == "unauthenticated") {
      // Only redirect if we're not already on the login page
      // This prevents redirect loops and false positives
      if (typeof window !== "undefined") {
        const currentPath = window.location.pathname;
        if (!currentPath.includes("/login") && !currentPath.includes("/register")) {
          clearAuthCookies();
          clearDeviceCredentials();
          // Use a small delay to prevent race conditions
          setTimeout(() => {
            window.location.href = basePath() + "/login";
          }, 100);
        }
      }
    }

    return response;
  },
  (error) => {
    if (error.response) {
      const { status } = error.response;
      const originalRequest = error.config;

      if (status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        return refreshAccessToken()
          .then((newAccessToken) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return request(originalRequest);
          })
          .catch((refreshError) => {
            clearAuthCookies();
            clearDeviceCredentials();
            redirectToLoginIfNeeded();
            return Promise.reject(refreshError);
          });
      }

      // Do not auto-logout on every 403 response. Many endpoints can return
      // authorization/business-rule 403 errors while the session is still valid.
    }

    if (axios.isAxiosError(error) && error.response?.status === 500) {
      error.handleGlobally && error.handleGlobally();
    }

    return Promise.reject(error);
  },
);

export const getMetaTitle = (title) => {
  return {
    title: getTitle(title),
    robots: getRobotsMetadata(),
    openGraph: {
      title: getTitle(title),
    },
  };
};

export const getTitle = (title = null) => {
  return title
    ? `${process.env.NEXT_PUBLIC_SITE_TITLE} - ${title}`
    : process.env.NEXT_PUBLIC_SITE_TITLE;
};

export const getCustomCaptcha = () => {
  return store.getState()?.captcha?.customCaptcha?.data;
};

export const getGoogleCaptcha = () => {
  return store?.getState()?.state?.captcha?.googleCaptcha?.data;
};

export const showEmailAddress = (email) => {
  return (
    email.substring(0, 1) + "***" + email.substring(email.indexOf("@") - 1 + 1)
  );
};

export function showMobileNumber(number) {
  return number.substring(0, 2) + "***" + number.substring(number.length - 2);
}

export const getUser = () => JSON.parse(getCookie("user_data") || "null");

export const getCountries = async () => {
  try {
    return (await request.get(ENDPOINTS.COUNTRIES)).data;
  } catch (error) {
    return [];
  }
};

export function showDateTime(date, format = "YYYY-MM-DD hh:mm A") {
  if (!date) {
    return "-";
  }

  const lang = getCookie("lang") || getDefaultLang();

  dayjs.tz.setDefault("UTC");
  dayjs.locale(lang);
  return dayjs(date).format(format);
}

function getDefaultLang() {
  return "en";
}

export function diffForHumans(date) {
  if (!date) {
    return "-";
  }

  const lang = getCookie("lang") || getDefaultLang();

  dayjs.locale(lang);
  return dayjs(date).fromNow();
}

export function getRelativeTime(date) {
  if (!date) return "";
  const now = new Date();
  const past = new Date(date);
  const diffTime = Math.abs(now - past);
  const diffMinutes = Math.floor(diffTime / (1000 * 60));
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  // Less than 1 hour: show "X mins ago"
  if (diffMinutes < 60) {
    if (diffMinutes === 0) return "Just now";
    if (diffMinutes === 1) return "1 min ago";
    return `${diffMinutes} mins ago`;
  }

  // Today but more than 1 hour: show time (e.g., "9:30 AM")
  if (diffDays === 0) {
    return showDateTime(date, "h:mm A");
  }

  // More than 1 day
  if (diffDays === 1) return "1 day ago";
  return `${diffDays} days ago`;
}

export function keyToTitle(text) {
  return text
    .replace(/[^A-Za-z0-9 ]/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

export function titleToKey(text) {
  return text.toLowerCase().replace(/ /g, "_");
}

export function getLastSegment(str) {
  if (!str) {
    return null;
  }

  const segments = str.split(".");
  return segments[segments.length - 1];
}

export function getFrontendLogo() {
  return getImage() + "logo_icon/frontend_logo.png?v1";
}

export function getLogo() {
  return getImage() + "logo_icon/logo.png?v1";
}

export function getFavicon() {
  return getImage() + "logo_icon/favicon.png?v1";
}

export function getAmount(amount, length = 0) {
  amount = parseFloat(amount);

  if (length > 0) {
    amount = amount.toFixed(length);
  } else {
    amount = amount.toFixed(2);
  }

  return amount;
}

export function findObject(object = [], key, value) {
  const index = object?.findIndex((row) => row?.[key] === value);

  if (index !== -1) {
    return {
      index,
      data: object?.[index],
    };
  } else {
    return null;
  }
}

export const getSEO = async ({
  description = null,
  image = null,
  keywords = null,
  title = null,
  url = null,
} = {}) => {
  const globalSeo = await fetch(
    process.env.NEXT_PUBLIC_API_URL + ENDPOINTS.SEO,
    {
      next: { revalidate: 300 },
    },
  ).then((res) => res.json());
  const seoContents = globalSeo?.data?.seo_content?.data_values;
  return {
    title: title || getTitle("Home"),
    description: description || seoContents?.description,
    keywords: keywords || seoContents?.keywords,
    openGraph: {
      title: title || getTitle("Home"),
      description: description || seoContents?.description,
      type: "website",
      url: url || process.env.NEXT_PUBLIC_API_URL,
      images: [image ? image : getImage() + "seo/" + seoContents?.image],
    },
  };
};

export const slug = (string) => {
  return string
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$|--+/g, "");
};

export const customPageData = async (slug) => {
  const getCustomPage = async (slug) => {
    const { data } = await request.get(`${ENDPOINTS.CUSTOM_PAGE}/${slug}`);
    return data;
  };

  return await getCustomPage(slug);
};

export const footerPageData = async (slug) => {
  const getFooterPage = async (slug) => {
    const { data } = await request.get(`${ENDPOINTS.FOOTER_PAGE}/${slug}`);
    return data;
  };

  return await getFooterPage(slug);
};

export const notifyToast = (data) => {
  const status = data?.status || "error";

  if (Array.isArray(data?.message)) {
    data?.message?.map((m) => toast[status](m));
  } else {
    data?.message?.[status]?.map((m) => {
      toast[status](m)[0];
    });
  }
};

export const toBase64 = (str) => {
  return btoa(unescape(encodeURIComponent(str)));
};

export const fromBase64 = (str) => {
  return decodeURIComponent(escape(atob(str)));
};

export function generateScript(script, shortcode) {
  let generatedScript = script;
  for (const [key, item] of Object.entries(shortcode)) {
    generatedScript = generatedScript.replace(
      new RegExp(`{{${key}}}`, "g"),
      item.value,
    );
  }
  return generatedScript;
}

/** Get image from backend url /assets/images/* */
export function getImage() {
  return `${process.env.NEXT_PUBLIC_API_URL}/assets/images/`;
}

/**
 * This helper function helps you to get an image for a section or from the /frontend directory
 */
// export const frontendImage = (src, section = "") => {
//     return `${getImage()}frontend/${section ? `${section}/` : ""}${src}`;
// };

export function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} Bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export const getFingerPrint = async () => {
  if (typeof window == "undefined") return null;

  const fp = await FingerprintJS.load();
  const result = await fp.get();
  return result.visitorId;
};

// Utility function to construct image URLs dynamically
export const frontendImage = (imageName, folder = "") => {
  return imageName
    ? `${process.env.NEXT_PUBLIC_API_URL}/assets/images/frontend/${
        folder ? `${folder}/` : ""
      }${imageName}`
    : "#";
};

export function basePath() {
  return process.env.NEXT_PUBLIC_AUTH_PREFIX == "/" ||
    !process.env.NEXT_PUBLIC_AUTH_PREFIX
    ? ""
    : process.env.NEXT_PUBLIC_AUTH_PREFIX;
}

export function calculateCharge(amount, charges) {
  if (!charges || charges.length === 0) return 0;
  const numericAmount = Number(amount);
  const first = charges[0];
  const last = charges[charges.length - 1];

  let chargeElement = null;

  for (let i = 0; i < charges.length; i++) {
    const current = charges[i];
    const next = charges[i + 1];

    if (numericAmount <= Number(first.amount)) {
      chargeElement = first;
      break;
    }

    if (numericAmount >= Number(last.amount)) {
      chargeElement = last;
      break;
    }

    if (
      next &&
      numericAmount > Number(current.amount) &&
      numericAmount <= Number(next.amount)
    ) {
      chargeElement = next;
      break;
    }
  }

  if (!chargeElement) return 0;

  let charge = Number(chargeElement.charge);

  if (chargeElement.type == 1) {
    charge = charge;
  } else {
    charge = (amount * charge) / 100;
  }

  return parseFloat(charge).toFixed(2);
}

export function calculateExchange({
  amount,
  recipientAmount,
  lastEdited,
  senderCountry,
  receiverCountry,
  setAmount,
  setRecipientAmount,
  selectedFacility,
}) {
  if (!senderCountry || !receiverCountry)
    return {
      conversionRate: 1,
      fees: 0,
      totalYouPay: 0,
    };

  // Find the rate
  let rate = 1;
  const rateData = senderCountry.conversion_rates?.find(
    (rate) => rate.to === receiverCountry.id,
  );
  if (rateData) {
    rate = rateData.rate;
  }

  // Find matching facility charge
  const matchedFacility = receiverCountry?.facility_charges?.find(
    (f) => f.facility_id === selectedFacility?.id,
  );

  // Get the charge array for the matched facility based on sender currency
  let charges = [];
  if (matchedFacility) {
    const senderCurrencyCode = senderCountry?.code?.toLowerCase(); // e.g., "gbp", "usd", "eur", "ngn"
    const chargeKey = `${senderCurrencyCode}Charge`; // e.g., "gbpCharge", "usdCharge"
    charges = matchedFacility[chargeKey] || [];
  }

  // Calculate charge only if matched facility exists
  const charge = matchedFacility && charges.length > 0 ? calculateCharge(amount, charges) : 0;

  // If last edited is amount or recipient amount
  if (lastEdited === "amount") {
    const recipientGets = amount * rate;
    setRecipientAmount(recipientGets > 0 ? recipientGets.toFixed(2) : "");
  } else if (lastEdited === "recipientAmount") {
    const senderAmount = recipientAmount / rate;
    setAmount(senderAmount > 0 ? senderAmount.toFixed(2) : "");
  }

  // Calculate total pay
  const totalYouPay = parseFloat(amount) + parseFloat(charge);

  return {
    conversionRate: parseFloat(rate) || 1,
    fees: parseFloat(charge) || 0,
    totalYouPay: totalYouPay,
  };
}

export function capitalize(str) {
  if (!str) return "";
  return str?.charAt(0)?.toUpperCase() + str?.slice(1)?.toLowerCase();
}

export function shortDescription(string, length = 120) {
  if (string.length <= length) return string;
  return string.substring(0, length).trim() + "...";
}

export const getStatusBadge = (
  status,
  depositStatus,
  docInput,
  docSubmitted,
) => {
  let text = "";
  let variant = "";

  if (depositStatus === 2 && docInput && docSubmitted) {
    text = "Document Submitted";
    variant = "primary";
  } else if (depositStatus === 2 && docInput && !docSubmitted) {
    text = "Document Needed";
    variant = "info";
  } else if (status === 0) {
    text = "Recipient's Info Needed";
    variant = "danger";
  } else if (status === 1) {
    text = "Transaction Incomplete";
    variant = "warning";
  } else if (status === 2) {
    text = "Manual Payment Incomplete";
    variant = "warning";
  } else if (status === 3 && depositStatus === 9) {
    text = "Under Review";
    variant = "dark";
  } else if (status === 3) {
    text = "Pending";
    variant = "info";
  } else if (status === 4) {
    text = "Payment Canceled";
    variant = "danger";
  } else if (status === 5) {
    text = "In Progress";
    variant = "primary";
  } else if (status === 6) {
    text = "Completed";
    variant = "success";
  } else if (status === 7) {
    text = "Failed";
    variant = "danger";
  } else if (status === 8) {
    text = "Transfer Under Review";
    variant = "black";
  } else if (status === 9) {
    text = "Transfer Cancelled";
    variant = "danger";
  }

  return <Badge text={text} variant={variant} />;
};

export const LOCAL_TRANSFER_STATUS = {
  DRAFT: 0,
  PENDING: 1,
  PROCESSING: 2,
  COMPLETED: 3,
  FAILED: 4,
  CANCELLED: 5,
  UNDER_REVIEW: 6,
  INCOMPLETE: 7,
  DELETED: 8,
};

export const getLocalTransferStatusBadge = (status) => {
  switch (status) {
    case LOCAL_TRANSFER_STATUS.DRAFT:
      return getStatusBadge(0);
    case LOCAL_TRANSFER_STATUS.PENDING:
      return getStatusBadge(3);
    case LOCAL_TRANSFER_STATUS.PROCESSING:
      return getStatusBadge(5);
    case LOCAL_TRANSFER_STATUS.COMPLETED:
      return getStatusBadge(6);
    case LOCAL_TRANSFER_STATUS.FAILED:
      return getStatusBadge(7);
    case LOCAL_TRANSFER_STATUS.CANCELLED:
      return getStatusBadge(9);
    case LOCAL_TRANSFER_STATUS.UNDER_REVIEW:
      return getStatusBadge(8);
    case LOCAL_TRANSFER_STATUS.INCOMPLETE:
      return getStatusBadge(1);
    case LOCAL_TRANSFER_STATUS.DELETED:
      return getStatusBadge(9);
    default:
      return getStatusBadge(status);
  }
};

export const isLocalTransferRow = (item) => {
  if (item?._isBelyftedTransfer === true) return false;

  return (
    item?.transaction_type === "local" ||
    item?.type === "local" ||
    item?.transfer_type === "local" ||
    item?.payment_type === "local" ||
    item?.local_transfer_id != null ||
    item?.recipient_details != null ||
    item?._isLocalTransfer === true
  );
};

/**
 * Builds the full set of security headers that the axios request interceptor
 * normally attaches automatically.  Use this when making raw `fetch()` calls
 * (e.g. SSE streams) that bypass the interceptor.
 *
 * @param {{
 *   method?: string,
 *   url?: string,
 *   params?: object,
 *   body?: unknown,
 *   forceIdempotencyKey?: boolean,
 * }} options
 * @returns {Promise<Record<string, string>>}
 */
export const buildApiHeaders = async ({
  method = "GET",
  url = "",
  params = {},
  body = null,
  forceIdempotencyKey = false,
} = {}) => {
  const headers = {};

  const token = getCookie("access-token");
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const normalizedMethod = getMethod(method);
  const { path, queryString } = buildPathAndQuery(url, params);
  const timestamp = Math.floor(Date.now() / 1000).toString();
  const nonce = generateUuid();
  const deviceId = await resolveDeviceId();

  headers["X-Timestamp"] = timestamp;
  headers["X-Nonce"] = nonce;
  headers["X-Device-ID"] = deviceId;
  headers["X-Fingerprint"] = deviceId;

  const isDeviceScopedRoute = shouldUseDeviceScopedSecurity(path);
  const hmacSecret = isDeviceScopedRoute
    ? getStoredDeviceSecret()
    : process.env.NEXT_PUBLIC_BELYFTED_HMAC_SECRET;

  if (hmacSecret) {
    const signature = createSignature({
      method: normalizedMethod,
      path,
      queryString,
      timestamp,
      nonce,
      deviceId,
      body: normalizeBody(body),
      secret: hmacSecret,
    });
    headers["X-Signature"] = signature;
  }

  const needsIdempotency =
    forceIdempotencyKey || shouldAttachIdempotencyKey(path, normalizedMethod);
  if (needsIdempotency) {
    const idempotencyKey = generateUuid();
    // Send both forms — endpoints vary in which header name they check
    headers["Idempotency-Key"] = idempotencyKey;
    headers["X-Idempotency-Key"] = idempotencyKey;
  }

  if (shouldAttachRequiredBooleanHeader(path)) {
    headers[REQUIRED_BOOLEAN_HEADER_KEY] = "true";
  }

  return headers;
};